import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  OnboardingLayout,
  OnboardingLayoutProps,
} from "../../components/OnboardingLayout";
import { Translations, i18n } from "../../i18n";
import { PasswordResetFormValues } from "./types";
import { validationSchema } from "./content";
import { TextInput } from "../../components/Input";
import { Spacing, SpacingSizes } from "../../components/Spacing";
import { PasswordValidator } from "./PasswordValidator";
import { Eye } from "../../assets";
import { useResetPassword } from "../../hooks/auth/useResetPassword";
import { ErrorBanner } from "../../services";
import { useHistory } from "react-router-dom";
import { Routes } from "../../types";

export const PasswordResetRoute = () => {
  const [showValidator, setShowValidator] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<PasswordResetFormValues>({
    defaultValues: {
      password: "",
      passwordConfirm: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const [resetPassword, { loading }] = useResetPassword();

  const { replace } = useHistory();

  const passwordController = register("password");
  const passwordConfirmController = register("passwordConfirm");

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async ({ password }: PasswordResetFormValues) => {
    try {
      await resetPassword({
        variables: {
          password,
          token,
        },
      });

      replace(Routes.PasswordResetConfirmation);
    } catch {
      ErrorBanner.show();
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.PasswordResetRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.SignUpRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
        loading,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <TextInput
        {...passwordController}
        label={i18n.t(Translations.LoginRoutePasswordLabel)}
        value={watch("password")}
        onChangeText={(text) => setValue("password", text)}
        type={showPassword ? "text" : "password"}
        error={errors.password?.message}
        rightIcon={<Eye />}
        onRightIconClick={toggleShowPassword}
        placeholder={i18n.t(Translations.SignUpRoutePasswordPlaceholder)}
        onFocusCallback={() => setShowValidator(true)}
        onBlurCallback={() => setShowValidator(false)}
      />
      <Spacing size={SpacingSizes.M} />
      <TextInput
        {...passwordConfirmController}
        label={i18n.t(Translations.LoginRoutePasswordConfirmLabel)}
        value={watch("passwordConfirm")}
        onChangeText={(text) => setValue("passwordConfirm", text)}
        type={showPassword ? "text" : "password"}
        error={errors.password?.message}
        placeholder={i18n.t(Translations.SignUpRoutePasswordConfirmRequired)}
        rightIcon={<Eye />}
        onRightIconClick={toggleShowPassword}
        onFocusCallback={() => setShowValidator(true)}
        onBlurCallback={() => setShowValidator(false)}
      />

      {showValidator ? (
        <PasswordValidator
          password={watch("password")}
          passwordConfirm={watch("passwordConfirm")}
        />
      ) : null}
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
